import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import enGB from 'date-fns/locale/en-GB';
import BookingPricePreview from './AddBooking/BookingPricePreview';
import PaymentModeSelector from './AddBooking/PaymentModeSelector';
import PendingReviewBanner from './AddBooking/PendingReviewBanner';
import AddonsSelection from './AddBooking/AddonsSelection';
import { maintenanceTypeIds, addonPayload, customAddonsValid } from './bookingTypeRules';
import { adminShortfallReason, isAdminWalletShort } from '../../ClientView/walletShortfall';
import LoadingPopup from '../../SharedView/LoadingPopup';
import './addBooking.css';
import './priceOverridePanel.css';

registerLocale('en-GB', enGB);

const LAUNDRY_TYPES = [1, 5, 6];
const AMENITIES_TYPES = [1, 5];
const NO_CLEANER_TYPES = [6, 7, 8, 12];
// All-Inclusive types — the first such booking on a linen-hire property gets
// the one-time AED 100 linen deposit added automatically by the backend.
const LINEN_DEPOSIT_TYPES = [1, 5];

const calculateDuration = (bedrooms, cleaners) => {
  switch (bedrooms) {
    case 0: return 2.5;
    case 1: return 3;
    case 2: return cleaners === 2 ? 2.5 : 4;
    case 3: return 3;
    case 4: return 4;
    default: return 2;
  }
};

const calculateEndTime = (start, duration) => {
  if (!start) return null;
  return new Date(new Date(start).getTime() + duration * 3600000);
};

const durationOptions = [
  { value: '1', label: '1 Hour' }, { value: '1.5', label: '1.5 Hours' },
  { value: '2', label: '2 Hours' }, { value: '2.5', label: '2.5 Hours' },
  { value: '3', label: '3 Hours' }, { value: '3.5', label: '3.5 Hours' },
  { value: '4', label: '4 Hours' }, { value: '4.5', label: '4.5 Hours' },
  { value: '5', label: '5 Hours' }, { value: '5.5', label: '5.5 Hours' },
  { value: '6', label: '6 Hours' },
];

const formatDateToString = (date) => {
  if (!(date instanceof Date)) date = new Date(date);
  const y = date.getFullYear(), mo = String(date.getMonth() + 1).padStart(2, '0'),
    d = String(date.getDate()).padStart(2, '0'), h = String(date.getHours()).padStart(2, '0'),
    mi = String(date.getMinutes()).padStart(2, '0'), s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${d} ${h}:${mi}:${s}`;
};

function AddBooking() {
  const [cleaners, setCleaners] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookingTypes, setBookingTypes] = useState([]);
  const [bookingStatus, setBookingStatus] = useState([]);
  const [addons, setAddons] = useState([]);
  const [unavailableCleaners, setUnavailableCleaners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [linenDepositStatus, setLinenDepositStatus] = useState(null);

  // Admin checkout selector + pending-review gate (payments Phase 3).
  const [paymentContext, setPaymentContext] = useState(null);   // { card_charge_enabled, active_card, wallet }
  const [pendingSummary, setPendingSummary] = useState(null);   // { requests, pending_bookings, counts }
  const [paymentMode, setPaymentMode] = useState('wallet');     // 'wallet' | 'card' | 'invoice_later'
  const [paymentModeTouched, setPaymentModeTouched] = useState(false);
  const [pendingAck, setPendingAck] = useState(false);
  const [liveTotal, setLiveTotal] = useState(0);

  const [formData, setFormData] = useState({
    booking_start: null, booking_end: null,
    client_id: '', property_id: '', type_id: '', status_id: '',
    booking_specific_notes: '', booking_warehouse_notes: '',
    required_cleaners: 1, duration: 2, cleaner_ids: [],
    is_laundry: false, is_amenities: false, selected_addons: [],
    custom_price_excl_vat: '',
  });

  const navigate = useNavigate();
  const isNoCleanerType = NO_CLEANER_TYPES.includes(formData.type_id);
  // Maintenance: looked up by name so we don't have to hardcode an ID. Hides
  // cleaners + laundry/amenities toggles and defaults duration to 1h.
  // Maintenance-like types (Maintenance, Swimming Pool Maintenance): looked
  // up by name, see bookingTypeRules.js.
  const maintenanceIds = maintenanceTypeIds(bookingTypes);
  const isMaintenanceType = maintenanceIds.includes(formData.type_id);
  // The invoice's main line for this type: "Maintenance Service" or
  // "Swimming Pool Maintenance Service" (items 808 / 1350).
  const maintenanceLineLabel = `${bookingTypes.find(t => t.id === formData.type_id)?.type || 'Maintenance'} Service`;
  const isPending = bookingStatus.find(s => String(s.id) === String(formData.status_id))?.status_name?.trim().toLowerCase() === 'pending';

  // Firm-warn gate: block Submit until the admin acknowledges the client's
  // pending items (payments Phase 3). Non-blocking otherwise.
  const pendingItemCount = pendingSummary
    ? (pendingSummary.counts?.requests || 0) + (pendingSummary.counts?.bookings || 0)
    : 0;
  const pendingGateBlocks = pendingItemCount > 0 && !pendingAck;

  // ZERO FLOOR (P4, owner demand #1). Submitting a wallet-mode booking the
  // wallet cannot cover would drive the client's AVAILABLE balance below zero.
  // The Wallet option is disabled for this case, but `paymentMode` can already
  // BE 'wallet' from an earlier, cheaper total — so the same condition gates
  // Submit. The refusal is stated on the button up front, never on click.
  const walletFloorBlocks = paymentMode === 'wallet'
    && !!paymentContext
    && isAdminWalletShort({
      cost: liveTotal,
      available: Number(paymentContext.wallet?.available_balance || 0),
    });
  const walletFloorReason = walletFloorBlocks
    ? adminShortfallReason({
      cost: liveTotal,
      available: Number(paymentContext.wallet?.available_balance || 0),
    })
    : null;

  // --- Data fetching ---
  useEffect(() => {
    (async () => {
      try {
        const [cR, clR, tR, sR, aR] = await Promise.all([
          axios.get('/api/team/role/?role=cleaner'),
          axios.get('/api/clients/active'),
          axios.get('/api/bookings/booking_types'),
          axios.get('/api/bookings/booking_status'),
          axios.get('/api/bookings/addons'),
        ]);
        setCleaners(cR.data); setClients(clR.data);
        setBookingTypes(tR.data); setBookingStatus(sR.data); setAddons(aR.data);
      } catch (e) { console.error('Error fetching data:', e); }
    })();
  }, []);

  // Linen deposit heads-up: linen-hire property + All-Inclusive type where no
  // deposit is outstanding → the backend will auto-add the AED 100 deposit.
  const selectedProperty = properties.find(p => p.id === formData.property_id);
  const linenDepositApplies = !!selectedProperty?.is_linen_hire && LINEN_DEPOSIT_TYPES.includes(formData.type_id);

  useEffect(() => {
    if (!linenDepositApplies || !formData.property_id) {
      setLinenDepositStatus(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/linen-deposits/property/${formData.property_id}`);
        if (!cancelled) setLinenDepositStatus(res.data);
      } catch (e) {
        console.error('Error fetching linen deposit status:', e);
        if (!cancelled) setLinenDepositStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [linenDepositApplies, formData.property_id]);

  const statusForThisProperty = linenDepositStatus
    && Number(linenDepositStatus.property_id) === Number(formData.property_id)
    ? linenDepositStatus : null;

  // The deposit belongs to the property's FIRST All-Inclusive cleaning. A unit
  // that has already had one — however many, and whether or not it was ever
  // charged — is past that moment and is never auto-charged. Same for a
  // property an admin has already removed it from. Mirrors the server gates in
  // linenDepositService.depositQuoteForNewBooking.
  const showLinenDepositNote = linenDepositApplies
    && statusForThisProperty
    && Number(statusForThisProperty.outstanding || 0) <= 0.009
    && !statusForThisProperty.has_all_inclusive_booking
    && !statusForThisProperty.deposit_waived;

  // Deposit already on record (e.g. charged manually on a Deep Cleaning with
  // linen sets before any All-Inclusive booking) → nothing will be added; the
  // admin sees WHERE it was charged instead.
  const existingDeposit = linenDepositApplies
    && statusForThisProperty
    && Number(statusForThisProperty.outstanding || 0) > 0.009
    ? (statusForThisProperty.deposits || [])[0] || null
    : null;

  // Admin override: don't auto-add the deposit on this booking (undoable).
  const [skipLinenDeposit, setSkipLinenDeposit] = useState(false);
  useEffect(() => { setSkipLinenDeposit(false); }, [formData.property_id, formData.type_id]);

  // Separate entity wallets: the wallet a booking charges is the PROPERTY's,
  // so the balance shown (and gated on) follows the chosen property. For
  // every other client this answers with the same main wallet as before.
  useEffect(() => {
    if (!formData.client_id || !formData.property_id) return;
    let stale = false;
    axios.get(`/api/bookings/admin/payment-context/${formData.client_id}`, { params: { property_id: formData.property_id } })
      .then((r) => { if (!stale) setPaymentContext(r.data); })
      .catch((e) => { console.error('payment-context failed:', e); });
    return () => { stale = true; };
  }, [formData.client_id, formData.property_id]);

  // Deposit is 100 AED PER BED (single + double), 125 AED per Super King
  // Size bed — min 1 standard bed when the unit has no bed data at all.
  const depositActive = showLinenDepositNote && !skipLinenDeposit;
  const linenDepositSkBeds = depositActive ? (parseInt(selectedProperty?.super_king_beds, 10) || 0) : 0;
  const linenDepositStdBedsRaw = depositActive
    ? (parseInt(selectedProperty?.single_beds, 10) || 0) + (parseInt(selectedProperty?.double_beds, 10) || 0)
    : 0;
  const linenDepositBeds = depositActive && (linenDepositStdBedsRaw + linenDepositSkBeds) === 0
    ? 1 : linenDepositStdBedsRaw;
  const linenDepositAmount = linenDepositBeds * 100 + linenDepositSkBeds * 125;

  useEffect(() => {
    if (!formData.booking_start || !formData.booking_end) return;
    (async () => {
      try {
        const res = await axios.get('/api/bookings/cleaners/availability', {
          params: { booking_start: formatDateToString(formData.booking_start), booking_end: formatDateToString(formData.booking_end) },
        });
        setUnavailableCleaners(res.data.map(c => c.cleaner_id));
      } catch (e) { console.error(e); }
    })();
  }, [formData.booking_start, formData.booking_end]);

  // Pre-select the checkout mode once we know the client's wallet and the live
  // total: wallet if it covers → otherwise INVOICE LATER (owner 2026-07-20 — a
  // client with no wallet balance defaults to invoice-later, not their saved
  // card; charging the card stays a deliberate MANUAL choice by staff). Only
  // while the admin hasn't manually overridden (paymentModeTouched).
  useEffect(() => {
    if (!paymentContext || paymentModeTouched) return;
    const available = Number(paymentContext.wallet?.available_balance || 0);
    let next;
    // AN EMPTY WALLET IS NEVER THE DEFAULT RAIL (2026-08-03): even when
    // nothing is due — a 0-AED change, or a booking priced to 0 by an
    // override — defaulting to "Wallet · AED 0.00 available" reads like the
    // wallet is about to be used. It also closed a float edge: a due of a
    // fraction of a fil passed `available >= due - 0.01` with 0 balance.
    if (available <= 0.005) next = 'invoice_later';
    else if (liveTotal > 0 && available >= liveTotal - 0.01) next = 'wallet';
    else if (liveTotal > 0) next = 'invoice_later';
    else next = 'wallet';
    setPaymentMode(next);
  }, [paymentContext, liveTotal, paymentModeTouched]);

  // --- Handlers ---
  const set = (updates) => setFormData(prev => ({ ...prev, ...updates }));

  const handlePaymentModeChange = (mode) => {
    setPaymentMode(mode);
    setPaymentModeTouched(true);
  };

  const handleTypeChange = (opt) => {
    const typeId = opt ? opt.value : '';
    const isCleaner = !NO_CLEANER_TYPES.includes(typeId);
    const isMaintenance = maintenanceIds.includes(typeId);
    set({
      type_id: typeId,
      required_cleaners: isMaintenance ? 0 : (isCleaner ? Math.max(1, formData.required_cleaners) : 0),
      cleaner_ids: isMaintenance ? [] : (isCleaner ? formData.cleaner_ids : []),
      duration: isMaintenance ? 1 : (isCleaner ? formData.duration : 0),
      booking_start: [6, 7, 8].includes(typeId) ? new Date(new Date().setHours(18, 0, 0, 0)) : formData.booking_start,
      is_laundry: isMaintenance ? false : LAUNDRY_TYPES.includes(typeId),
      is_amenities: isMaintenance ? false : AMENITIES_TYPES.includes(typeId),
      // Clear custom price when switching away from Maintenance, leave empty
      // when switching to Maintenance so the admin must enter it explicitly.
      custom_price_excl_vat: isMaintenance ? '' : '',
    });
  };

  const handleClientChange = async (opt) => {
    const clientId = opt ? opt.value : '';
    set({ client_id: clientId, property_id: '' });
    // Reset the checkout selector + gate for the newly-chosen client.
    setPaymentContext(null);
    setPendingSummary(null);
    setPaymentMode('wallet');
    setPaymentModeTouched(false);
    setPendingAck(false);
    if (clientId) {
      try {
        const res = await axios.get(`/api/properties/active?clientId=${clientId}`);
        setProperties(res.data);
      } catch (e) { console.error(e); setProperties([]); }
      // Payment context (card availability + wallet balance) and the pending-review
      // summary — best-effort, never block the form on these.
      axios.get(`/api/bookings/admin/payment-context/${clientId}`)
        .then((r) => setPaymentContext(r.data))
        .catch((e) => { console.error('payment-context failed:', e); setPaymentContext(null); });
      axios.get(`/api/bookings/admin/pending-summary/${clientId}`)
        .then((r) => setPendingSummary(r.data))
        .catch((e) => { console.error('pending-summary failed:', e); setPendingSummary(null); });
    } else { setProperties([]); }
  };

  const handlePropertyChange = (opt) => {
    if (!opt) {
      set({
        property_id: '',
        required_cleaners: isMaintenanceType ? 0 : 1,
        duration: isMaintenanceType ? 1 : 2,
      });
      return;
    }
    const prop = properties.find(p => p.id === opt.value);
    set({
      property_id: prop.id,
      // Maintenance has no cleaner assignment + fixed 1h default — don't let
      // the property's bedroom-based defaults override it.
      required_cleaners: isMaintenanceType ? 0 : (prop.required_cleaners || 1),
      duration: isMaintenanceType ? 1 : calculateDuration(prop.bedrooms, prop.required_cleaners || 1),
    });
  };

  const handleDateChange = (date) => {
    const start = [6].includes(formData.type_id) ? new Date(date).setHours(18, 0, 0, 0) : date;
    const end = isNoCleanerType ? start : calculateEndTime(start, formData.duration);
    set({ booking_start: new Date(start), booking_end: new Date(end), required_cleaners: isNoCleanerType ? 0 : formData.required_cleaners });
  };

  const handleDurationChange = (opt) => {
    const dur = parseFloat(opt.value);
    set({ duration: dur, booking_end: calculateEndTime(formData.booking_start, dur) });
  };

  const handleCleanersCountChange = (e) => {
    const count = parseInt(e.target.value, 10) || 1;
    const prop = properties.find(p => p.id === formData.property_id);
    const dur = prop ? calculateDuration(prop.bedrooms, count) : formData.duration;
    set({ required_cleaners: count, duration: dur, booking_end: calculateEndTime(formData.booking_start, dur) });
  };


  /**
   * PRICE OVERRIDE AT CREATION (PLAN_price_overrides.md, 2026-08-03).
   * Managers only; edited inside the Booking Cost Preview table itself and
   * sent with the create, so the wallet charges the discounted price from
   * the very first charge.
   */
  const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
  const [overrideDraft, setOverrideDraft] = useState({}); // item_id -> {kind, value, reason}

  // Only rows that actually SET something, and each field independently — a
  // draft with no kind must never be sent as `custom_rate: NaN` (2026-08-30).
  const overridesForSubmit = () => Object.entries(overrideDraft)
    .filter(([, d]) => (d.kind === '%' || d.kind === 'aed') && d.value !== '')
    .map(([item_id, d]) => ({
      item_id: parseInt(item_id, 10),
      ...(d.kind === '%' ? { discount_percent: parseFloat(d.value) } : { custom_rate: parseFloat(d.value) }),
      ...(d.reason ? { reason: d.reason } : {}),
    }));

  const handleSubmit = async () => {
    if (!customAddonsValid(formData.selected_addons)) {
      alert('Each custom add-on needs a name, a price (0 or more) and a quantity.');
      return;
    }
    try {
      setIsLoading(true);
      const res = await axios.post('/api/bookings', {
        ...formData,
        // Force 0 cleaners + empty cleaner list for Maintenance, regardless of
        // any state drift from property change / mid-edit type swaps.
        required_cleaners: isMaintenanceType ? 0 : formData.required_cleaners,
        cleaner_ids: isMaintenanceType ? [] : formData.cleaner_ids,
        // Net price entered by admin for Maintenance bookings — null otherwise.
        // Trigger uses this as the line-item rate; Zoho adds VAT on top.
        custom_price_excl_vat: isMaintenanceType
          ? (parseFloat(formData.custom_price_excl_vat) || null)
          : null,
        booking_start: formatDateToString(formData.booking_start),
        booking_end: formatDateToString(formData.booking_end),
        selected_addons: formData.selected_addons.map(addonPayload),
        skip_linen_deposit: skipLinenDeposit,
        // Admin checkout selector (payments Phase 3): wallet | card | invoice_later.
        payment_mode: paymentMode,
        // Manager-only; the route strips it from anyone else's payload.
        price_overrides: overridesForSubmit(),
      });
      // A created booking is NOT the same as a paid booking. Reflect what
      // actually happened per the chosen payment mode.
      const wallet = res.data?.wallet;
      setFeedbackMessage(bookingResultMessage(wallet, res.data?.linen_deposit_added));
    } catch (e) {
      console.error(e);
      // A card availability guard rejects BEFORE creating the booking (400).
      setFeedbackMessage(e?.response?.status === 400 && e?.response?.data?.error
        ? `Error: ${e.response.data.error}`
        : 'Error creating booking.');
    }
    finally { setIsLoading(false); }
  };

  // Turn the service's wallet result into an admin-facing message.
  const bookingResultMessage = (wallet, linenAdded) => {
    if (wallet && wallet.charged === false) {
      if (wallet.reason === 'invoice_later') {
        return 'Booking created — to be invoiced later (no payment taken).';
      }
      if (wallet.via === 'card') {
        const map = {
          no_active_card: 'no active saved card',
          declined: 'the card was declined',
          gateway_unavailable: 'card charging is unavailable',
          wallet_post_failed: 'the card was charged but the wallet ledger needs reconciling (tech notified)',
        };
        return `⚠️ Booking created, but card payment did not complete — ${map[wallet.reason] || 'card payment failed'}. The booking was set to Pending.`;
      }
      return wallet.reason === 'deposit_sync_error'
        ? '⚠️ Booking created, but the client was NOT charged — wallet deposit sync error (contact tech support). The booking was set to Pending.'
        : '⚠️ Booking created, but the client was NOT charged — insufficient wallet balance. The booking was set to Pending.';
    }
    if (wallet && wallet.charged === true && wallet.via === 'card') {
      return 'Booking created and paid by the client’s saved card.';
    }
    return linenAdded
      ? 'Booking created successfully! One-time linen deposit (AED 100 per bed, VAT-free) was added to this booking.'
      : 'Booking created successfully!';
  };

  return (
    <div className="add-booking-container">
      <div className="ab-header">
        <h2>Add New Booking</h2>
        <p className="ab-subtitle">Create a new booking for a client property.</p>
      </div>

      <form className="new-booking-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

        {/* Card 1: Booking type, status, toggles */}
        <div className="ab-card">
          <div className="ab-card__head">
            <h3 className="ab-card__title">Booking Details</h3>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Booking Type</label>
              <Select
                options={bookingTypes.map(t => ({ value: t.id, label: t.type }))}
                onChange={handleTypeChange} placeholder="Select type" required
              />
            </div>
            <div className="form-group">
              <label>Booking Status</label>
              <Select
                options={bookingStatus.map(s => ({ value: s.id, label: s.status_name }))}
                onChange={(opt) => {
                  const name = opt ? String(opt.label || '') : '';
                  set({ status_id: opt ? opt.value : '', pending_reason: name.trim().toLowerCase() === 'pending' ? formData.pending_reason : '' });
                }}
                placeholder="Select status" required
              />
            </div>
            {isMaintenanceType && (
              <div className="form-group">
                <label>Price (excl. VAT, AED)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.custom_price_excl_vat}
                  onChange={(e) => set({ custom_price_excl_vat: e.target.value })}
                  placeholder="e.g. 250.00"
                  required
                />
                {/* The VAT-inclusive figure right where the price is typed
                    (owner, 2026-10-04) — what the client actually pays for
                    the service line, before any add-ons. */}
                <div className="ab-vatIncl">
                  {parseFloat(formData.custom_price_excl_vat) > 0
                    ? <>Incl. VAT (5%): <strong>AED {(Math.round(parseFloat(formData.custom_price_excl_vat) * 105) / 100).toFixed(2)}</strong></>
                    : 'VAT (5%) is added on the Zoho invoice.'}
                </div>
              </div>
            )}
            {!isMaintenanceType && (
              <div className="form-group laundry">
                <label>Laundry</label>
                <label className="switch">
                  <input type="checkbox" checked={formData.is_laundry} onChange={() => set({ is_laundry: !formData.is_laundry })} />
                  <span className="slider round"></span>
                </label>
              </div>
            )}
            {!isMaintenanceType && (
              <div className="form-group amenities">
                <label>Amenities</label>
                <label className="switch">
                  <input type="checkbox" checked={formData.is_amenities} onChange={() => set({ is_amenities: !formData.is_amenities })}
                    disabled={AMENITIES_TYPES.includes(formData.type_id)} />
                  <span className="slider round"></span>
                </label>
              </div>
            )}
          </div>
          {isPending && (
            <div style={{ marginTop: 14, maxWidth: 400 }}>
              <div className="form-group">
                <label>Pending Reason</label>
                <input type="text" value={formData.pending_reason || ''} onChange={(e) => set({ pending_reason: e.target.value })} placeholder="Enter pending reason" required />
              </div>
            </div>
          )}
        </div>

        {/* Card 2: Client & Property */}
        <div className="ab-card">
          <div className="ab-card__head">
            <h3 className="ab-card__title">Client & Property</h3>
          </div>
          <div className="ab-grid">
            <div className="form-group">
              <label>Client</label>
              <Select
                options={clients.map(c => ({ value: c.id, label: c.display_name || `${c.first_name} ${c.last_name}` }))}
                onChange={handleClientChange} placeholder="Select a client" required
              />
            </div>
            <div className="form-group">
              <label>Property</label>
              <Select
                options={properties.map(p => ({ value: p.id, label: `${p.unit_number} - ${p.address_line_1} (${p.bedrooms === 0 ? 'S' : p.bedrooms}, ${p.bathrooms})` }))}
                onChange={handlePropertyChange} placeholder="Select a property" isDisabled={!formData.client_id} required
              />
            </div>
          </div>
        </div>

        {/* Card 3: Schedule & Cleaners */}
        <div className="ab-card">
          <div className="ab-card__head">
            <h3 className="ab-card__title">Schedule & Cleaners</h3>
          </div>

          {!isNoCleanerType && (
            <div className="ab-grid--3">
              {!isMaintenanceType && (
                <div className="form-group">
                  <label>Required Cleaners</label>
                  <input type="number" value={formData.required_cleaners} onChange={handleCleanersCountChange} min="1" required />
                </div>
              )}
              <div className="form-group">
                <label>Start Time</label>
                <DatePicker selected={formData.booking_start} onChange={handleDateChange}
                  showTimeSelect timeFormat="HH:mm" timeIntervals={30} timeCaption="Time"
                  locale="en-GB" dateFormat="dd/MM/yyyy HH:mm" placeholderText="Select Start Time" required />
              </div>
              <div className="form-group">
                <label>Duration</label>
                <Select options={durationOptions} value={durationOptions.find(o => o.value === String(formData.duration))}
                  onChange={handleDurationChange} placeholder="Select duration" required />
              </div>
            </div>
          )}

          {[6].includes(formData.type_id) && (
            <div style={{ maxWidth: 300 }}>
              <div className="form-group">
                <label>Select Date</label>
                <DatePicker selected={formData.booking_start} onChange={handleDateChange}
                  locale="en-GB" dateFormat="dd/MM/yyyy" placeholderText="Select Date" required />
              </div>
            </div>
          )}

          {[7, 8].includes(formData.type_id) && (
            <div style={{ maxWidth: 300 }}>
              <div className="form-group">
                <label>Date & Time</label>
                <DatePicker selected={formData.booking_start} onChange={handleDateChange}
                  showTimeSelect timeFormat="HH:mm" timeIntervals={30} timeCaption="Time"
                  locale="en-GB" dateFormat="dd/MM/yyyy HH:mm" placeholderText="Select Date & Time" required />
              </div>
            </div>
          )}

          {!isNoCleanerType && !isMaintenanceType && formData.required_cleaners > 0 && (
            <div className="ab-grid--4" style={{ marginTop: 14 }}>
              {Array.from({ length: formData.required_cleaners }).map((_, i) => (
                <div key={i} className="form-group">
                  <label>Cleaner {i + 1}</label>
                  <Select
                    options={cleaners.map(c => ({
                      value: c.id,
                      label: `${c.first_name} ${c.last_name}${unavailableCleaners.includes(c.id) ? ' (Unavailable)' : ''}`,
                      isDisabled: unavailableCleaners.includes(c.id),
                    }))}
                    onChange={(opt) => {
                      const ids = [...formData.cleaner_ids];
                      ids[i] = opt ? opt.value : null;
                      set({ cleaner_ids: ids });
                    }}
                    value={formData.cleaner_ids[i] ? { value: formData.cleaner_ids[i], label: cleaners.find(c => c.id === formData.cleaner_ids[i])?.first_name } : null}
                    placeholder={`Cleaner ${i + 1}`} isClearable
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Card 4: Add-ons & Notes */}
        <div className="ab-card">
          <div className="ab-card__head">
            <h3 className="ab-card__title">Add-ons & Notes</h3>
          </div>
          {showLinenDepositNote && !skipLinenDeposit && (
            <div className="linen-deposit-note">
              <span>
                ℹ️ A one-time linen deposit of AED {linenDepositAmount} ({[
                  linenDepositBeds > 0 ? `${linenDepositBeds} bed${linenDepositBeds > 1 ? 's' : ''} × AED 100` : null,
                  linenDepositSkBeds > 0 ? `${linenDepositSkBeds} super king × AED 125` : null,
                ].filter(Boolean).join(' + ')}, VAT-free)
                will be added to this booking automatically — this is the unit's first All-Inclusive cleaning.
                Removing it here means it is never auto-charged for this unit.
              </span>
              <button type="button" className="linen-deposit-note__btn" onClick={() => setSkipLinenDeposit(true)}>
                Remove
              </button>
            </div>
          )}
          {showLinenDepositNote && skipLinenDeposit && (
            <div className="linen-deposit-note linen-deposit-note--muted">
              <span>
                Linen deposit will <strong>not</strong> be added — and because the deposit belongs to a
                unit's first All-Inclusive cleaning, it won't be charged automatically on later bookings
                for this unit either. You can still add it deliberately as an add-on at any time.
              </span>
              <button type="button" className="linen-deposit-note__btn" onClick={() => setSkipLinenDeposit(false)}>
                Add it back
              </button>
            </div>
          )}
          {/* Already removed on an earlier booking — explain why nothing is
              being added, so it doesn't look like a missing charge. */}
          {linenDepositApplies && statusForThisProperty?.deposit_waived && (
            <div className="linen-deposit-note linen-deposit-note--muted">
              <span>
                No linen deposit will be added — it was removed for this unit earlier, so it is never
                charged automatically. Add it as an add-on if you need to charge it.
              </span>
            </div>
          )}
          {/* Only worth flagging when the deposit was charged WITHOUT an
              All-Inclusive cleaning (e.g. manually on a Deep Cleaning) — for
              properties that already had an AI booking this is business as
              usual and needs no banner. */}
          {existingDeposit && !statusForThisProperty.has_all_inclusive_booking && (
            <div className="linen-deposit-note linen-deposit-note--warn">
              <span>
                ⚠️ This property hasn't had an All-Inclusive cleaning yet, but the linen deposit
                (AED {Number(statusForThisProperty.outstanding).toFixed(0)}) was <strong>already charged</strong>
                {existingDeposit.booking_id
                  ? <> at <a href={`/bookings/${existingDeposit.booking_id}`}>booking #{existingDeposit.booking_id}</a></>
                  : ' (recorded manually)'}
                {' '}— it will not be charged again.
              </span>
            </div>
          )}
          {/* Catalogue add-ons + custom add-ons (typed name and net price,
              shown as such on the invoice) — shared with Edit Booking. */}
          <AddonsSelection addons={addons} formData={formData} setFormData={setFormData} />
          <div className="notes" style={{ marginTop: 14 }}>
            <div className="form-group">
              <label>Booking Notes</label>
              <textarea value={formData.booking_specific_notes} onChange={(e) => set({ booking_specific_notes: e.target.value })} placeholder="Specific notes for this booking" />
            </div>
            <div className="form-group">
              <label>Warehouse Notes</label>
              <textarea value={formData.booking_warehouse_notes} onChange={(e) => set({ booking_warehouse_notes: e.target.value })} placeholder="Warehouse notes for this booking" />
            </div>
          </div>
        </div>

        {/* Price Preview */}
        {(formData.property_id || formData.type_id) && (
          <BookingPricePreview
            selectedAddons={formData.selected_addons} clientId={formData.client_id}
            propertyId={formData.property_id} properties={properties}
            typeId={formData.type_id} statusId={formData.status_id}
            bookingStart={formData.booking_start} bookingEnd={formData.booking_end}
            requiredCleaners={formData.required_cleaners} isAmenities={formData.is_amenities}
            duration={formData.duration}
            isMaintenance={isMaintenanceType}
            maintenanceLabel={maintenanceLineLabel}
            customPriceExclVat={formData.custom_price_excl_vat}
            autoLinenDepositBeds={linenDepositBeds}
            autoLinenDepositSkBeds={linenDepositSkBeds}
            onTotal={setLiveTotal}
            showWallet={false}
            /* Managers and supervisors, never admins (2026-08-31). The server
               enforces the same rule; this only decides whether the control is
               offered. */
            canOverride={userRole === 'manager' || userRole === 'supervisor'}
            overrides={overrideDraft}
            onOverridesChange={setOverrideDraft}
          />
        )}

        {/* Checkout selector (payments Phase 3) — once a client is chosen. */}
        {formData.client_id && (
          <div className="ab-card">
            <div className="ab-card__head">
              <h3 className="ab-card__title">Payment</h3>
            </div>
            <PaymentModeSelector
              mode={paymentMode}
              onChange={handlePaymentModeChange}
              context={paymentContext}
              total={liveTotal}
            />
          </div>
        )}

        {/* Pending-review gate — steer the admin to resolve pending items first. */}
        {formData.client_id && (
          <PendingReviewBanner
            summary={pendingSummary}
            acknowledged={pendingAck}
            onToggleAck={setPendingAck}
          />
        )}

        <div className="ab-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={pendingGateBlocks || walletFloorBlocks}
            title={
              walletFloorBlocks
                ? walletFloorReason
                : (pendingGateBlocks ? 'Review the client’s pending items first, then tick the acknowledgement.' : undefined)
            }
          >
            Submit Booking
          </button>
        </div>

      </form>

      <LoadingPopup isLoading={isLoading} feedbackMessage={feedbackMessage}
        onClose={() => {
          setFeedbackMessage('');
          // Navigate whenever a booking was created — success, the not-charged
          // warning, invoice-later, and card-paid all count (staying on the
          // filled form invites a double-submit).
          if (feedbackMessage.startsWith('Booking created')
            || feedbackMessage.startsWith('⚠️ Booking created')) navigate('/bookings');
        }} />
    </div>
  );
}

export default AddBooking;
